import { api } from "./client";

/** ----- Temperature zones ----- */
export const ZONES = [
  "zone_1",
  "zone_2",
  "zone_3",
  "zone_4",
  "zone_5",
  "zone_6",
  "zone_7",
  "zone_8",
  "zone_9",
  "zone_10",
  "zone_11",
  "zone_12",
  "zone_13",
  "zone_14",
  "zone_15",
  "zone_16",
  "zone_17",
  "zone_18", // L SCREEN
  "zone_19", // R SCREEN
  "zone_20", // DIE HEAD
] as const;

export type ZoneKey = (typeof ZONES)[number];

/** ----- Q water-flow status zones (boolean) ----- */
export const Q_KEYS = ["q02", "q03", "q04", "q05", "q06", "q07"] as const;

export type QKey = (typeof Q_KEYS)[number];

/** A unified key set the UI may show in the selector */
export const ALL_KEYS = [...ZONES, ...Q_KEYS] as const;
export type MetricKey = (typeof ALL_KEYS)[number];

/** ----- Types returned from API ----- */
export type FoamReading = {
  id: number;
  ts_utc: string;
  /**
   * The PLC's port, which is what identifies the machine. The stored rows keep
   * the full `ip:port` for diagnostics, but the API reports the port alone —
   * otherwise moving a PLC to a new IP splits its history into two machines.
   */
  plc: string;

  // booleans
  butane_control: boolean;
  q02: boolean;
  q03: boolean;
  q04: boolean;
  q05: boolean;
  q06: boolean;
  q07: boolean;

  // floats
  sv?: number | null;
  pv?: number | null;
  low_pressure?: number | null;
  high_pressure_1?: number | null;
  /**
   * Despite the name this carries the cooling water temperature in °C — the
   * same signal the foam page charts as "Water Temperature".
   */
  high_pressure_2?: number | null;

  // High-pressure alarms. Null on rows written before 2026-09-23, when the
  // worker started collecting them, or if the PLC read failed.
  /** Warning setpoint in psi (VD74, REAL). */
  hp_warning_setpoint?: number | null;
  /** Emergency-shutdown setpoint in psi (VD78, REAL). */
  hp_shutdown_setpoint?: number | null;
  /** V60.1 — high pressure above the warning setpoint. */
  hp_warning_active?: boolean | null;
  /** V60.2 — high pressure above the emergency stop setpoint. */
  hp_shutdown_active?: boolean | null;

  /**
   * Age of this reading in seconds, computed server-side. Only /foam/latest
   * fills it in: ts_utc comes back Nashville-local naive, so a browser
   * subtracting it from `now` would be wrong by the UTC offset.
   */
  age_seconds?: number;
} & Partial<Record<ZoneKey, number | null>>;

export type FoamReadingsResponse = { total: number; items: FoamReading[] };

/** Newest foam winder reading. Fields map to V-memory on the winder PLC. */
export type FoamWinderStatusRow = {
  ts_utc: string;
  /** The PLC's port — identity, not address. See FoamReading.plc. */
  plc: string;
  /** V12.1 */
  status: boolean | null;
  /** V12.2 — true = auto, false = manual. */
  auto_mode: boolean | null;
  /** REAL at VD50; meaning not confirmed yet. */
  vd50: number | null;
  /** Age computed server-side, so browser clock/timezone can't skew it. */
  age_seconds: number;
};

/**
 * Newest winder reading, or null before the worker has written one.
 * The worker writes every poll, so `age_seconds` is a direct staleness signal.
 */
export function getFoamWinderLatest(signal?: AbortSignal) {
  return api
    .get<FoamWinderStatusRow | null>("/foam/winder/latest", { signal })
    .then((r) => r.data);
}

/** Downsampled series point. ts_ms = epoch millis UTC; booleans come back as 0/1. */
export type FoamSeriesPoint = {
  ts_ms: number;
  sv?: number | null;
  pv?: number | null;
  low_pressure?: number | null;
  high_pressure_1?: number | null;
  high_pressure_2?: number | null;
  butane_control?: number | null;
  q02?: number | null;
  q03?: number | null;
  q04?: number | null;
  q05?: number | null;
  q06?: number | null;
  q07?: number | null;
  hp_warning_active?: number | null;
  hp_shutdown_active?: number | null;
  hp_warning_setpoint?: number | null;
  hp_shutdown_setpoint?: number | null;
} & Partial<Record<ZoneKey, number | null>>;

export type FoamSeriesResponse = {
  bucket_seconds: number; // 0 => raw
  count: number;
  downsampled: boolean;
  items: FoamSeriesPoint[];
};

/** ----- Labels ----- */
export const DEFAULT_ZONE_LABELS: Record<ZoneKey, string> = ZONES.reduce(
  (acc, zk, idx) => {
    acc[zk] = `Zone ${idx + 1}`;
    return acc;
  },
  {} as Record<ZoneKey, string>
);

// Friendly names for the three new zones:
DEFAULT_ZONE_LABELS.zone_18 = "L SCREEN";
DEFAULT_ZONE_LABELS.zone_19 = "R SCREEN";
DEFAULT_ZONE_LABELS.zone_20 = "DIE HEAD";

export const DEFAULT_Q_LABELS: Record<QKey, string> = {
  q02: "Water Zone 1",
  q03: "Water Zone 2",
  q04: "Water Zone 3",
  q05: "Water Zone 4",
  q06: "Water Zone 5",
  q07: "Water Zone 6",
};

export const DEFAULT_LABELS: Record<MetricKey, string> = {
  ...(DEFAULT_ZONE_LABELS as Record<string, string>),
  ...(DEFAULT_Q_LABELS as Record<string, string>),
} as Record<MetricKey, string>;

/** ----- Endpoints ----- */

export function getFoamLatest(signal?: AbortSignal) {
  return api.get<FoamReading[]>("/foam/latest", { signal }).then((r) => r.data);
}

export function getFoamReadings(params: {
  plc?: string;
  startISO: string;
  endISO: string;
  order?: "asc" | "desc";
  signal?: AbortSignal;
}) {
  const { signal, plc, startISO, endISO, order } = params;
  const query = {
    ...(plc ? { plc_ip: plc } : {}),
    start: startISO,
    end: endISO,
    ...(order ? { order } : {}),
  };
  return api
    .get<FoamReadingsResponse>("/foam/readings", { params: query, signal })
    .then((r) => r.data);
}

/**
 * GET /foam/series — downsampled (bucket-averaged) time series for charting.
 * The server picks an adaptive bucket width so the window yields ~maxPoints points;
 * narrow windows (or bucketSeconds=0) return full 60s resolution.
 */
export function getFoamSeries(params: {
  plc?: string;
  startISO: string;
  endISO: string;
  maxPoints?: number;
  bucketSeconds?: number; // 0 forces raw
  signal?: AbortSignal;
}) {
  const { signal, plc, startISO, endISO, maxPoints, bucketSeconds } = params;
  const query = {
    ...(plc ? { plc_ip: plc } : {}),
    start: startISO,
    end: endISO,
    ...(maxPoints ? { max_points: maxPoints } : {}),
    ...(bucketSeconds != null ? { bucket_seconds: bucketSeconds } : {}),
  };
  return api
    .get<FoamSeriesResponse>("/foam/series", { params: query, signal })
    .then((r) => r.data);
}

/** GET /foam/zones/labels -> { zone_18: "L SCREEN", ... } (sparse) */
export async function getFoamZoneLabels(signal?: AbortSignal) {
  const { data } = await api.get<Record<string, string>>("/foam/zones/labels", {
    signal,
  });
  const merged: Record<MetricKey, string> = { ...DEFAULT_LABELS };
  for (const key of Object.keys(data)) {
    merged[key as MetricKey] = data[key]!;
  }
  return merged;
}

/** Convenience with fallback */
export async function getResolvedZoneLabels(signal?: AbortSignal) {
  try {
    return await getFoamZoneLabels(signal);
  } catch {
    return { ...DEFAULT_LABELS };
  }
}
